<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { coverageAlertsApi, type CoverageAlert } from '@/api/coverage-alerts';
import type { Shift } from '@/api/shifts';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useGroupFilterStore } from '@/stores/group-filter';
import FilterMultiSelect from '@/components/filter-multi-select.vue';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ShiftForm from '@/components/shifts/actions/shift-form.vue';
import { addDays, todayIso } from '@/utils/calendar-events';
import CoverageAlertCloseDialog from './coverage-alert-close-dialog.vue';

// Planned visits about to start without a shift (CoverageAlerts::Scan), and how each was
// resolved. Abiertas: act on them (create the shift, or close with a reason). Resueltas: the
// history over a slot-date range.
const HISTORY_DAYS = 7;

const tab = ref<'open' | 'resolved'>('open');
const to = ref(todayIso());
const from = ref(addDays(to.value, -HISTORY_DAYS));

const { groupIds } = storeToRefs(useGroupFilterStore());
const { accessibleCareers } = useAccessibleCareers();
const careerIn = ref<string[]>([]);

const filters = computed(() => (tab.value === 'open' ?
  { status: 'open' as const, groupIdIn: groupIds.value, careerIn: careerIn.value } :
  {
    status: 'resolved' as const, from: from.value, to: to.value,
    groupIdIn: groupIds.value, careerIn: careerIn.value,
  }));

// Rows stack into cards on phones, so "Crear turno" and "Cerrar" stay in view.
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 50 });

const { data: alerts, isFetching, isError } = useQuery({
  queryKey: computed(() => ['coverageAlerts', filters.value.status, filters.value]),
  queryFn: () => coverageAlertsApi.index(filters.value),
});

const openHeaders = [
  { title: 'Inicio', key: 'humanSlotStartsAt', sortable: false },
  { title: 'Paciente', key: 'patientFullName', sortable: false },
  { title: 'Especialidad', key: 'humanCareer', sortable: false },
  { title: 'Grupos', key: 'groupNames', sortable: false },
  { title: '', key: 'actions', sortable: false, align: 'end' as const },
];
const resolvedHeaders = [
  { title: 'Inicio', key: 'humanSlotStartsAt', sortable: false },
  { title: 'Paciente', key: 'patientFullName', sortable: false },
  { title: 'Especialidad', key: 'humanCareer', sortable: false },
  { title: 'Resolución', key: 'humanAasmState', sortable: false },
  { title: 'Cerrada', key: 'humanResolvedAt', sortable: false },
];
const headers = computed(() => (tab.value === 'open' ? openHeaders : resolvedHeaders));

const emptyText = computed(() => (tab.value === 'open' ?
  'No hay visitas por comenzar sin turno.' :
  'No hay alertas resueltas en estas fechas.'));

const closing = ref<CoverageAlert | null>(null);

// "Crear turno" opens the shift form on the slot: patient, date and times prefilled, the
// caregiver picker narrowed to the alert career. The alert closes itself on the next scan.
const creatingFor = ref<CoverageAlert | null>(null);
const showCreate = computed({
  get: () => creatingFor.value !== null,
  set: (value: boolean) => {
    if (!value) creatingFor.value = null;
  },
});
const createShift = computed(() => (creatingFor.value ?
  {
    patientId: creatingFor.value.patientId,
    startTime: creatingFor.value.slotStartTime,
    endTime: creatingFor.value.slotEndTime ?? undefined,
  } as unknown as Shift :
  undefined));
const shiftCreated = ref(false);

const queryClient = useQueryClient();
function onShiftCreated() {
  shiftCreated.value = true;
  queryClient.invalidateQueries({ queryKey: ['coverageAlerts'] });
}

function resolutionDetail(alert: CoverageAlert): string {
  if (alert.aasmState === 'covered') return 'Se creó un turno para la visita';

  return [alert.humanResolutionReason, alert.resolutionNote].filter(Boolean).join(' · ');
}
</script>

<template>
  <v-container
    fluid
    class="pa-4"
  >
    <div class="mb-4">
      <h1 class="text-h5 font-weight-bold">
        Alertas de cobertura
      </h1>
      <div class="text-body-2 text-medium-emphasis">
        Visitas planificadas que están por comenzar sin turno asignado, y cómo se resolvieron.
      </div>
    </div>

    <v-tabs
      v-model="tab"
      color="primary"
      class="mb-4"
    >
      <v-tab value="open">
        Abiertas
      </v-tab>
      <v-tab value="resolved">
        Resueltas
      </v-tab>
    </v-tabs>

    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="4"
        md="3"
      >
        <group-filter-select
          v-model="groupIds"
          variant="outlined"
          density="compact"
          hide-details
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        md="3"
      >
        <filter-multi-select
          v-model="careerIn"
          :items="accessibleCareers"
          label="Especialidad"
          variant="outlined"
          density="compact"
          hide-details
        />
      </v-col>
      <template v-if="tab === 'resolved'">
        <v-col
          cols="6"
          sm="2"
        >
          <v-text-field
            v-model="from"
            type="date"
            label="Desde"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
        <v-col
          cols="6"
          sm="2"
        >
          <v-text-field
            v-model="to"
            type="date"
            label="Hasta"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
      </template>
    </v-row>

    <v-alert
      v-if="isError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-2"
    >
      No se pudieron cargar las alertas. Revisa que el rango no supere los 93 días.
    </v-alert>

    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :items="alerts ?? []"
      :loading="isFetching"
      :no-data-text="emptyText"
      item-value="id"
    >
      <template #[`item.groupNames`]="{ item }">
        {{ item.groupNames.join(', ') }}
      </template>
      <template #[`item.humanAasmState`]="{ item }">
        <v-chip
          size="small"
          :color="item.aasmState === 'covered' ? 'green-darken-3' : 'grey-darken-3'"
          variant="tonal"
        >
          {{ item.humanAasmState }}
        </v-chip>
        <div class="text-caption text-medium-emphasis">
          {{ resolutionDetail(item) }}
        </div>
      </template>
      <template #[`item.humanResolvedAt`]="{ item }">
        {{ item.humanResolvedAt }}
        <div
          v-if="item.resolvedByEmail"
          class="text-caption text-medium-emphasis"
        >
          {{ item.resolvedByEmail }}
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex ga-2 justify-end">
          <v-btn
            size="small"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-calendar-plus"
            @click="creatingFor = item"
          >
            Crear turno
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            @click="closing = item"
          >
            Cerrar
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <coverage-alert-close-dialog
      :alert="closing"
      @close="closing = null"
    />

    <responsive-dialog
      v-model="showCreate"
      max-width="800"
      scrollable
    >
      <shift-form
        v-if="creatingFor"
        :shift="createShift"
        :date="creatingFor.slotDate"
        :caregiver-careers="[creatingFor.career]"
        mode="new"
        source="date"
        @close="creatingFor = null"
        @success="onShiftCreated"
      />
    </responsive-dialog>

    <v-snackbar
      v-model="shiftCreated"
      color="success"
    >
      Turno creado. La alerta se cerrará sola en los próximos minutos.
    </v-snackbar>
  </v-container>
</template>
